import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { box, button, input, label } from '../shared/styles';

/*
 * Who reads the experiment's thermal captures (docs/FIELD_AI_VISION_SPEC.md
 * section 44; routes/experiment_thermal.py): its level, its named
 * collaborators and its owner. Everyone who opens the tab sees them; the
 * owner, a collection manager or a system admin (`may_manage`) changes
 * them. A manager may hand the experiment on -- the one way a private
 * experiment's captures are reached when its owner leaves -- never read
 * them.
 */
export const LEVELS = ['private', 'collaborators', 'team', 'bank'];

export default function ThermalSharing({ experimentId, level, owner, mayManage, onChanged }) {
  const { t } = useTranslation('experiments');
  const [people, setPeople] = useState(null);
  const [chosen, setChosen] = useState(level);
  const [adding, setAdding] = useState('');
  const [successor, setSuccessor] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(() => api.get(`/experiments/${experimentId}/collaborators`)
    .then(({ data }) => setPeople(data)).catch((e) => setMessage({ kind: 'error', text: errorText(e) })),
  [experimentId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setChosen(level); }, [level]);

  const act = async (request, done) => {
    setMessage(null);
    try {
      const { data } = await request();
      if (data?.collaborators) setPeople((p) => ({ ...p, ...data }));
      setMessage({ kind: 'ok', text: done });
      onChanged?.();
      return true;
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
      return false;
    }
  };
  const saveLevel = () => act(() => api.put(`/experiments/${experimentId}/thermal-visibility`, { visibility: chosen }),
    t('thermal.sharing.levelSaved'));
  const add = async () => {
    if (await act(() => api.post(`/experiments/${experimentId}/collaborators`, { user_id: adding }),
      t('thermal.sharing.added'))) setAdding('');
  };
  const remove = (userId) => act(() => api.post(`/experiments/${experimentId}/collaborators/${userId}/remove`, {}),
    t('thermal.sharing.removed'));
  const transfer = async () => {
    if (await act(() => api.put(`/experiments/${experimentId}/owner`, { user_id: successor, reason }),
      t('thermal.sharing.transferred'))) {
      setSuccessor('');
      setReason('');
      load();
    }
  };

  const named = people?.collaborators || [];
  const candidates = people?.candidates || [];
  return (
    <section style={box} aria-labelledby="thermal-sharing-title" data-testid="thermal-sharing">
      <h4 id="thermal-sharing-title" style={{ marginTop: 0 }}>{t('thermal.sharing.title')}</h4>
      <p style={{ fontSize: 13, marginTop: 0 }}>{t('thermal.sharing.owner', { name: owner?.name || '' })}</p>
      {mayManage ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={label}>{t('thermal.sharing.level')}
            <select style={input} value={chosen} onChange={(e) => setChosen(e.target.value)}
              data-testid="thermal-level">
              {LEVELS.map((l) => <option key={l} value={l}>{t(`thermal.levels.${l}`)}</option>)}
            </select>
          </label>
          <button type="button" style={{ ...button('var(--c-primary)'), marginBottom: 10 }} disabled={chosen === level}
            onClick={saveLevel} data-testid="thermal-level-save">{t('thermal.sharing.saveLevel')}</button>
        </div>
      ) : (
        <p style={{ fontSize: 13 }} data-testid="thermal-level-read">
          {t('thermal.sharing.levelIs', { level: t(`thermal.levels.${level}`) })}</p>
      )}
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t(`thermal.levelHelp.${chosen}`)}</p>

      <h5 style={{ margin: '8px 0 4px' }}>{t('thermal.sharing.collaborators')}</h5>
      {named.length === 0 && <p style={{ fontSize: 13, margin: 0 }}>{t('thermal.sharing.none')}</p>}
      {named.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13 }} data-testid="thermal-collaborators">
          {named.map((c) => (
            <li key={c.user_id}>
              {c.name}
              {mayManage && (
                <button type="button" style={{ marginInlineStart: 8 }} onClick={() => remove(c.user_id)}
                  aria-label={t('thermal.sharing.removeOne', { name: c.name })}
                  data-testid={`thermal-collaborator-remove-${c.user_id}`}>{t('thermal.sharing.remove')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {mayManage && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          <label style={label}>{t('thermal.sharing.add')}
            <select style={input} value={adding} onChange={(e) => setAdding(e.target.value)}
              data-testid="thermal-collaborator-pick">
              <option value="">—</option>
              {candidates.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
            </select>
          </label>
          <button type="button" style={{ marginBottom: 10 }} disabled={!adding} onClick={add}
            data-testid="thermal-collaborator-add">{t('thermal.sharing.addButton')}</button>
        </div>
      )}

      {mayManage && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>{t('thermal.sharing.transfer')}</summary>
          <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('thermal.sharing.transferHelp')}</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={label}>{t('thermal.sharing.transferTo')}
              <select style={input} value={successor} onChange={(e) => setSuccessor(e.target.value)}
                data-testid="thermal-owner-pick">
                <option value="">—</option>
                {[...named, ...candidates].map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
            </label>
            <label style={{ ...label, flex: 1, minWidth: 180 }}>{t('thermal.sharing.transferReason')}
              <input style={input} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000}
                data-testid="thermal-owner-reason" />
            </label>
            <button type="button" style={{ marginBottom: 10 }} disabled={!successor || reason.trim().length < 3}
              onClick={transfer} data-testid="thermal-owner-save">{t('thermal.sharing.transferButton')}</button>
          </div>
        </details>
      )}
      {message && (
        <p role={message.kind === 'ok' ? 'status' : 'alert'} data-testid="thermal-sharing-message"
          style={{ margin: '6px 0 0', fontSize: 13, color: message.kind === 'ok' ? undefined : 'var(--c-danger-fg)' }}>
          {message.text}</p>
      )}
    </section>
  );
}
